import AutoComplete, {
  OptionType,
  SingleAutocompleteResponse,
  Sizes,
} from '@/components/AutoComplete';
import { StyledSubItemIcon } from '@/components/TopNavBar/styles';
import { companyEntityList } from '@/store/companyEntity/companyEntityThunk';
import { fetchActiveTransactionIds } from '@/store/global/globalThunks';
import { Autocomplete, TextField, Tooltip } from '@mui/material';
import { PagesPermissions } from '@/models/Permissions/enum';
import { useAppDispatch, useAppSelector, useUserHasAtLeastOnePermission } from '@store/hooks';
import { RootState } from '@store/store';
import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { ICONS_TYPES } from '../Icon';

import {
  ButtonWrapper,
  ContentContainer,
  Divider,
  IconButton,
  InlineRow,
  PropertiesIcon,
  Root,
  Section,
  SectionHeader,
  StyledAutocompleteWrapper,
  StyledIcon,
  StyledPopover,
} from './styles';
import { EntityOptionType, NavbarSearchPopoverProps } from './types';

const NavbarSearchPopover = ({ className }: NavbarSearchPopoverProps) => {
  const dispatch = useAppDispatch();
  const companyEntities = useAppSelector(
    (state: RootState) => state.companyEntity.entityList,
  );
  const activeTransactionIds = useAppSelector(
    (state: RootState) => state.global.activeTransactionIds,
  );
  const [anchorEl, setAnchorEl] = useState<HTMLDivElement | null>(null);
  const [open, setOpen] = useState(false);
  const [selectedTrade, setSelectedTrade] =
    useState<SingleAutocompleteResponse>(null);
  const [selectedEntity, setSelectedEntity] = useState<EntityOptionType | null>(
    null,
  );

  const [tradeInputValue, setTradeInputValue] = useState('');

  const hasPricerPermission = useUserHasAtLeastOnePermission([
    PagesPermissions.irPricer,
    PagesPermissions.fxPricer,
    PagesPermissions.inflationPricer,
    PagesPermissions.commodityPricer,
  ]);

  // Create a Set for O(1) lookup to validate if ID exists
  const activeTransactionIdsSet = useMemo(
    () => new Set(activeTransactionIds),
    [activeTransactionIds],
  );

  // Only show matching IDs as user types - limit to 20 for performance
  const tradeOptions = useMemo(() => {
    if (!tradeInputValue) {
      return [];
    }

    return activeTransactionIds
      .filter((id) => String(id).startsWith(tradeInputValue))
      .slice(0, 20) // Only show first 20 matches
      .sort((a, b) => b - a);
  }, [activeTransactionIds, tradeInputValue]);
  const entityOptions = useMemo(
    () =>
      companyEntities?.map((e) => ({
        value: e.comp_entity_id,
        label: `${e.company_name}: ${e.comp_entity_name}`,
        comp_entity_id: e.comp_entity_id,
        company_name: e.company_name,
        comp_entity_name: e.comp_entity_name,
      })) || [],
    [companyEntities],
  );

  const handleClick = useCallback((event: React.MouseEvent<HTMLDivElement>) => {
    event.stopPropagation();
    event.preventDefault();
    setAnchorEl(event.currentTarget);
    setOpen(true);
  }, []);
  const handleClose = useCallback(() => {
    setAnchorEl(null);
    setOpen(false);
  }, []);
  const handleTradeSelect = useCallback(
    (selected: SingleAutocompleteResponse) => {
      setSelectedTrade(selected);
    },
    [],
  );
  const handleEntitySelect = useCallback(
    (selected: SingleAutocompleteResponse) => {
      setSelectedEntity(selected);
    },
    [],
  );
  const handleOpenEntity = useCallback(() => {
    if (!selectedEntity || !selectedEntity.value) return;

    const url = `/portfolio/entity/${selectedEntity.value}`;
    window.open(url, '_blank');
  }, [selectedEntity]);
  const handleOpenTradeDetail = useCallback(() => {
    if (!selectedTrade || !selectedTrade.value) return;

    const url = `/portfolio/transaction/${selectedTrade.value}`;
    window.open(url, '_blank');
  }, [selectedTrade]);
  const handleOpenTradePricer = useCallback(() => {
    if (!selectedTrade || !selectedTrade.value) return;

    const url = `/hedging-center/pricer/${selectedTrade.value}`;
    window.open(url, '_blank');
  }, [selectedTrade]);

  useEffect(() => {
    if (!open) {
      setSelectedTrade(null);
      setSelectedEntity(null);
      setTradeInputValue('');
    } else {
      // Refresh transaction IDs whenever the dropdown opens
      dispatch(fetchActiveTransactionIds());
    }
  }, [open, dispatch]);

  useEffect(() => {
    // Initial load of company entities
    dispatch(companyEntityList(null));
  }, [dispatch]);

  return (
    <Root className={className}>
      <ButtonWrapper onClick={handleClick} open={open}>
        <PropertiesIcon
          icon={ICONS_TYPES.Search}
          size={18}
          open={open}
          rotate={90}
        />
      </ButtonWrapper>
      <StyledPopover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}>
        {open && <ContentContainer>
          {/* Company Entity Section */}
          <Section>
            <SectionHeader>
              <StyledSubItemIcon icon={ICONS_TYPES.Building} size={14} />
              Company Entity
            </SectionHeader>

            <InlineRow>
              <StyledAutocompleteWrapper>
              <AutoComplete<OptionType, false>
                name="entity-search"
                label="Search Entity"
                options={entityOptions}
                value={selectedEntity}
                onChange={handleEntitySelect}
                size={Sizes.medium}
                fullWidth
              />
              </StyledAutocompleteWrapper>

              <Tooltip
                title={
                  selectedEntity
                    ? `Open ${selectedEntity.label}`
                    : 'Select an entity first'
                }>
                <IconButton
                  $disabled={!selectedEntity}
                  onClick={handleOpenEntity}>
                  <StyledIcon icon={ICONS_TYPES.Link} size={16} />
                </IconButton>
              </Tooltip>
            </InlineRow>
          </Section>
          <Divider />
          {/* Trade Section */}
          <Section>
            <SectionHeader>
              <StyledSubItemIcon icon={ICONS_TYPES.Link} size={14} />
              Trade Operations
            </SectionHeader>
            <InlineRow>
              <StyledAutocompleteWrapper>
                <Autocomplete
                  freeSolo
                  options={tradeOptions}
                  inputValue={tradeInputValue}
                  value={selectedTrade?.value ?? null}
                  onInputChange={(_, newInputValue) => setTradeInputValue(newInputValue)}
                  onChange={(_, newValue) => {
                    if (typeof newValue === 'number') {
                      handleTradeSelect({ value: newValue, label: String(newValue) });
                    } else if (newValue === null) {
                      handleTradeSelect(null);
                    }
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Search Trade ID"
                      placeholder="Type transaction ID..."
                      size="small"
                    />
                  )}
                  fullWidth
                  size="small"
                />
              </StyledAutocompleteWrapper>
              {hasPricerPermission && (
                <Tooltip
                  title={
                    selectedTrade
                      ? `Open Trade ${selectedTrade.value} in Pricer`
                      : 'Select a trade first'
                  }>
                  <IconButton
                    $disabled={!selectedTrade}
                    onClick={handleOpenTradePricer}>
                    <StyledIcon icon={ICONS_TYPES.ShieldDown} size={16} />
                  </IconButton>
                </Tooltip>
              )}

              <Tooltip
                title={
                  selectedTrade
                    ? `Open Trade ${selectedTrade.value} Details`
                    : 'Select a trade first'
                }>
                <IconButton
                  $disabled={!selectedTrade}
                  onClick={handleOpenTradeDetail}>
                  <StyledIcon icon={ICONS_TYPES.Link} size={16} />
                </IconButton>
              </Tooltip>
            </InlineRow>
          </Section>
        </ContentContainer>}
      </StyledPopover>
    </Root>
  );
};

export default NavbarSearchPopover;
